import { useRef, useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AudioLines, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import UploadCard from "@/components/keyfinder/UploadCard";
import AnalyzingCard from "@/components/keyfinder/AnalyzingCard";
import ResultCard from "@/components/keyfinder/ResultCard";
import HistoryList from "@/components/keyfinder/HistoryList";
import PaywallModal from "@/components/keyfinder/PaywallModal";
import UserMenu from "@/components/keyfinder/UserMenu";
import GuestBanner from "@/components/keyfinder/GuestBanner";
import { useAuth } from "@/contexts/AuthContext";

const MAX_BYTES = 100 * 1024 * 1024;
const ALLOWED_EXT = ["mp3", "wav", "m4a", "ogg", "flac", "aac", "aiff", "aif", "webm", "mp4"];

export default function KeyFinder() {
  const { user, refresh, logout } = useAuth();
  const [file, setFile] = useState(null);
  const [phase, setPhase] = useState("upload");
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallReason, setPaywallReason] = useState(null);
  const fileInputRef = useRef(null);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await api.get("/history", { params: { limit: 50 } });
      setHistory(res.data || []);
    } catch (err) {
      // Non-blocking — history just won't render. Log for dev visibility.
      console.warn("History fetch failed:", err?.message);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const validateAndSetFile = useCallback((f) => {
    if (!f) return;
    const ext = f.name.split(".").pop().toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      toast.error("Unsupported file type", {
        description: `Use one of: ${ALLOWED_EXT.join(", ")}`,
      });
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error("File too large", {
        description: `Max size is 100 MB. Your file is ${(f.size / 1024 / 1024).toFixed(1)} MB.`,
      });
      return;
    }
    setFile(f);
  }, []);

  const handleAnalyze = async (fileArg) => {
    const target = fileArg || file;
    if (!target) return;

    // Pre-flight paywall check based on cached user state (avoids waste)
    if (user && !user.has_active_subscription &&
        (user.free_analyses_used ?? 0) >= (user.free_analyses_limit ?? 3)) {
      setPaywallReason("free_limit");
      setPaywallOpen(true);
      return;
    }

    setFile(target);
    setPhase("analyzing");
    try {
      const formData = new FormData();
      formData.append("file", target);
      const res = await api.post("/analyze", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setResult(res.data);
      setPhase("result");
      fetchHistory();
      refresh();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 402 || (detail && detail.paywall)) {
        setPaywallReason("free_limit");
        setPaywallOpen(true);
        setPhase("upload");
        refresh();
        return;
      }
      const msg = (typeof detail === "string" ? detail : detail?.message) || err?.message || "Something went wrong.";
      toast.error("Analysis failed", { description: msg });
      setPhase("upload");
    }
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    setPhase("upload");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSelectHistory = (item) => {
    setResult(item);
    setPhase("result");
  };

  const handleDeleteHistory = async (id) => {
    try {
      await api.delete(`/history/${id}`);
      setHistory((prev) => prev.filter((h) => h.id !== id));
      toast.success("Removed from history");
    } catch (err) {
      toast.error("Failed to delete", {
        description: err?.response?.data?.detail || err?.message || "Try again.",
      });
    }
  };

  const handleClearHistory = async () => {
    try {
      await api.delete("/history");
      setHistory([]);
      toast.success("History cleared");
    } catch (err) {
      toast.error("Failed to clear", {
        description: err?.response?.data?.detail || err?.message || "Try again.",
      });
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col items-center px-4 sm:px-8 py-10 relative">
      {/* Header */}
      <header className="w-full max-w-md flex items-center justify-between mb-8 relative z-10">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-[#0A0A0A] border border-white/10 flex items-center justify-center">
            <AudioLines className="w-5 h-5 text-[#FF2D55]" strokeWidth={2.2} />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-display font-black text-white text-xl tracking-tight" data-testid="app-title">
              KeyFinder
            </span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
              musical key detector
            </span>
          </div>
        </div>
        <UserMenu
          user={user}
          onLogout={logout}
          onUpgrade={() => {
            setPaywallReason("upgrade");
            setPaywallOpen(true);
          }}
        />
      </header>

      <main className="w-full max-w-md flex flex-col gap-6 relative z-10">
        {phase === "upload" && (
          <GuestBanner
            user={user}
            onLogin={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/dashboard";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            onUpgrade={() => {
              setPaywallReason("upgrade");
              setPaywallOpen(true);
            }}
          />
        )}

        <AnimatePresence mode="wait">
          {phase === "upload" && (
            <motion.div key="upload" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }}>
              <UploadCard
                file={file}
                onFile={validateAndSetFile}
                onAnalyze={handleAnalyze}
                onReset={() => setFile(null)}
                fileInputRef={fileInputRef}
              />
            </motion.div>
          )}
          {phase === "analyzing" && (
            <motion.div key="analyzing" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }}>
              <AnalyzingCard fileName={file?.name} />
            </motion.div>
          )}
          {phase === "result" && result && (
            <motion.div key="result" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.4 }}>
              <ResultCard result={result} onReset={handleReset} />
            </motion.div>
          )}
        </AnimatePresence>

        {phase === "upload" && (
          <HistoryList
            items={history}
            onDelete={handleDeleteHistory}
            onClear={handleClearHistory}
            onSelect={handleSelectHistory}
          />
        )}

        <p className="text-center text-xs text-zinc-600 mt-2 font-body leading-relaxed" data-testid="footer-note">
          Audio files are analyzed and discarded immediately. Only key results are stored.
        </p>
      </main>

      <footer className="w-full mt-16 pt-10 pb-8 flex flex-col items-center gap-3 relative z-10" data-testid="brand-footer">
        <div className="text-[9px] uppercase tracking-[0.4em] text-zinc-700 font-mono">a product by</div>
        <a href="#" aria-label="Taurus Technology" data-testid="taurus-logo-link" className="group flex flex-col items-center gap-2 transition-opacity hover:opacity-90">
          <img
            src="https://customer-assets.emergentagent.com/job_pitch-finder-46/artifacts/9xawcs4m_ChatGPT%20Image%20Jun%2018%2C%202026%2C%2010_21_31%20PM.png"
            alt="Taurus Technology"
            data-testid="taurus-logo"
            className="h-24 sm:h-28 w-auto opacity-80 group-hover:opacity-100 transition-opacity drop-shadow-[0_0_24px_rgba(255,255,255,0.06)] select-none"
            draggable="false"
          />
        </a>
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-600 font-mono">
          © {new Date().getFullYear()} Taurus Technology · All rights reserved
        </div>
      </footer>

      <PaywallModal
        open={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        user={user}
        reason={paywallReason}
      />
    </div>
  );
}
